if (!customElements.get('aplovio-gift-recipient')) {
  customElements.define('aplovio-gift-recipient', class extends HTMLElement {
    connectedCallback() {
      if (this.abort || !window.AplovioLayers) return;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.form = this.closest('form');
      this.fields = this.querySelector('[data-recipient-fields]');
      this.inputs = [...this.fields.querySelectorAll('input,textarea')];
      this.toggle = this.querySelector('[data-recipient-toggle]');
      this.trigger = this.querySelector('[data-gift-open]');
      this.layer = this.querySelector('aplovio-layer');
      this.dialog = this.layer.querySelector('dialog');
      this.email = this.querySelector('[data-recipient-email]');
      this.date = this.fields.querySelector('[type=date]');
      this.committed ||= this.snapshot();
      this.querySelector('[data-gift-fields-slot]').append(this.fields);
      this.querySelector('[data-gift-native]').hidden = true;
      this.trigger.hidden = false;
      const localDate = date => [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
      const today = new Date();
      const last = new Date();
      last.setDate(last.getDate() + 90);
      this.date.min = localDate(today);
      this.date.max = localDate(last);
      this.querySelector('[data-recipient-offset]').value = String(today.getTimezoneOffset());
      this.querySelector('[data-recipient-offset]').disabled = false;
      this.committed.values[this.querySelector('[data-recipient-offset]').name] = String(today.getTimezoneOffset());
      this.restore();
      const resize = () => {
        if (!this.dialog.open || !matchMedia('(max-width:989px)').matches) return;
        const viewport = window.visualViewport;
        if (viewport && viewport.scale === 1) {
          this.dialog.style.setProperty('--ap-purchase-viewport', viewport.height + 'px');
          this.dialog.style.setProperty('--ap-purchase-keyboard', Math.max(0, innerHeight - viewport.height - viewport.offsetTop) + 'px');
        }
      };
      window.visualViewport?.addEventListener('resize',resize,options);
      window.visualViewport?.addEventListener('scroll',resize,options);
      this.trigger.addEventListener('click', () => this.layer.open(this.trigger), options);
      this.layer.addEventListener('aplovio:layer-open', event => {
        if (event.target !== this.dialog) return;
        this.apply(this.draft || this.committed);
        this.fields.disabled = false;
        this.email.required = true;
        this.trigger.setAttribute('aria-expanded','true');
        this.querySelector('[data-gift-remove]').hidden = !this.committed.active;
        resize();
      }, options);
      this.layer.addEventListener('aplovio:layer-close', event => {
        if (event.target !== this.dialog) return;
        this.draft = this.snapshot();
        this.restore();
        this.trigger.setAttribute('aria-expanded','false');
        this.dialog.style.removeProperty('--ap-purchase-viewport');
        this.dialog.style.removeProperty('--ap-purchase-keyboard');
      }, options);
      this.querySelector('[data-gift-save]').addEventListener('click', () => this.save(), options);
      this.querySelector('[data-gift-remove]').addEventListener('click', () => {
        this.committed.active = false;
        this.draft = null;
        this.restore();
        this.layer.close('remove-recipient');
      }, options);
      this.form.addEventListener('submit', event => {
        if (this.dialog.open) {
          event.preventDefault();
          this.save();
        }
      }, {...options,capture:true});
      this.form.addEventListener('invalid', event => {
        if (!this.inputs.includes(event.target)) return;
        event.preventDefault();
        this.report(event.target);
        if (!this.dialog.open) this.layer.open(this.trigger);
        const first = this.inputs.find(input => !input.validity.valid);
        if (first === event.target) {
          const disclosure = first.closest('details');
          if (disclosure) window.AplovioLayers.disclose(disclosure, disclosure.querySelector('.ap-purchase-disclosure__body'), true, {animate:false});
          first.focus();
        }
      }, {...options,capture:true});
      this.fields.addEventListener('input', event => {
        if (event.target.getAttribute('aria-invalid') === 'true' && event.target.validity.valid) this.report(event.target);
        this.updateCount();
      }, options);
      this.fields.addEventListener('focusout', event => {
        if (event.target.value && this.inputs.includes(event.target)) this.report(event.target);
      }, options);
      this.querySelector('[data-gift-clear-date]').addEventListener('click', () => {
        this.date.value = '';
        this.report(this.date);
        this.updateCount();
      }, options);
    }

    snapshot() {
      return {active: this.toggle.checked, values: Object.fromEntries(this.inputs.map(input => [input.name, input.value]))};
    }

    apply(snapshot) {
      this.toggle.checked = snapshot.active;
      for (const input of this.inputs) input.value = snapshot.values[input.name] || '';
      this.updateCount();
    }

    restore() {
      this.apply(this.committed);
      this.fields.disabled = !this.committed.active;
      this.email.required = this.committed.active;
      this.querySelectorAll('[data-gift-error-for]').forEach(error => error.hidden = true);
      this.inputs.forEach(input => input.removeAttribute('aria-invalid'));
      let summary = this.dataset.addLabel;
      if (this.committed.active) {
        const name = this.committed.values['properties[Recipient name]'] || this.committed.values['properties[Recipient email]'];
        let delivery = this.dataset.deliveryLabel;
        const date = this.committed.values['properties[Send on]'];
        if (date) {
          const formatted = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {dateStyle:'medium'}).format(new Date(date + 'T12:00:00'));
          delivery = this.dataset.dateLabel.replace('[date]', formatted);
        }
        summary = this.dataset.toLabel.replace('[recipient]',name) + ' · ' + delivery;
      }
      this.querySelector('[data-gift-summary]').textContent = summary;
    }

    updateCount() {
      const message = this.fields.querySelector('textarea');
      this.querySelector('[data-gift-count]').textContent = `${message.value.length} / ${message.maxLength}`;
      this.querySelector('[data-gift-clear-date]').hidden = !this.date.value;
    }

    report(input) {
      const error = [...this.querySelectorAll('[data-gift-error-for]')].find(node => node.dataset.giftErrorFor === input.id);
      if (!error) return;
      error.hidden = input.validity.valid;
      error.textContent = input === this.email ? this.dataset.emailError : this.dataset.dateError;
      if (input.validity.valid) input.removeAttribute('aria-invalid');
      else input.setAttribute('aria-invalid','true');
    }

    save() {
      if (!this.dialog.open || this.dialog.dataset.layerPhase === 'closing') return;
      const invalid = this.inputs.filter(input => !input.checkValidity());
      if (invalid.length) return;
      this.committed = this.snapshot();
      this.committed.active = true;
      this.draft = null;
      this.restore();
      this.layer.close('gift-saved');
    }

    disconnectedCallback() {
      if (this.dialog?.open) {
        this.draft = this.snapshot();
        this.restore();
      }
      this.abort?.abort();
      this.abort = null;
    }
  });
}
